import { EmployeeExtended, ImportTypeEnum } from '@pxtra-client-lib-legacy/gen';
import { employeeStruct } from '@pxtra-client-lib-legacy';
import { makeAutoObservable } from 'mobx';
import { WorkSheet } from 'xlsx';
import { t, TFunction } from 'i18next';
import isEmail from 'validator/lib/isEmail';
import { FileWithPreview } from '../../../../lib/components';

export type RowWithErrors = {
  id?: number;
  column: string;
  preview: string | unknown;
  property?: string;
  isMapped?: boolean;
  address?: string;
  hasError?: boolean;
  errorMessage?: string;
};

const getOptionsArray = <T extends EmployeeExtended | EmployeeExtended[keyof EmployeeExtended]>(
  obj: T,
  prefix = '',
  t: TFunction
) => {
  let options: Array<{ value: string; text: string }> = [];

  for (const key in obj) {
    if (
      /(benefit|user|picture|missing_fields|app_settings|data_complete)/.test(key) ||
      key === 'id' ||
      key === 'status'
    ) {
      continue;
    }
    if (/(department|location|cost_center)/.test(key) && prefix === 'position_data__') {
      continue;
    }

    if (obj[key] !== null && typeof obj[key] === 'object') {
      options = [...options, ...getOptionsArray(obj[key], key + '__', t)];
    } else {
      options.push({ value: prefix + key, text: t(`employee_props:${key}`) });
    }
  }
  return options;
};

class CreateImportStore {
  private _type: ImportTypeEnum | undefined;
  private _columnHeaders: Array<string> = [];
  private _mappingRows: Array<RowWithErrors> = [];
  private _fileData?: WorkSheet;
  private _options: Array<{ value: string; text: string }> = [];
  private _emailColumn?: string;
  private _mappingObject: Record<string, string> = {};
  private _file?: FileWithPreview;
  private _error?: string;
  public encoded_string?: string;

  get type() {
    return this._type;
  }

  set type(type: ImportTypeEnum | undefined) {
    this._type = type;
  }

  get columnHeaders() {
    return this._columnHeaders;
  }

  get mappingRows() {
    return this._mappingRows;
  }

  get fileData() {
    return this._fileData;
  }

  set fileData(data: WorkSheet | undefined) {
    this._fileData = data;
    if (data) {
      // Set column headers
      const headers = Object.keys(data).filter((cell) => /^[A-Z]+1$/.test(cell));
      this._columnHeaders = headers.map((cell) => data[cell].v);

      // Set initial rows
      this._mappingRows = headers.map((head, index) => {
        return {
          id: index,
          address: head,
          column: data[head].v,
          preview: data[head.replace('1', '2')]?.v
        };
      });

      // auto map columns
      this.mapColumns();
    }
  }

  get options() {
    return this._options;
  }

  get emailColumn() {
    return this._emailColumn ?? '';
  }

  set emailColumn(address: string) {
    this._emailColumn = address.replace(/[0-9]/g, '');
  }

  get mappingObject() {
    return this._mappingObject;
  }

  get file() {
    return this._file;
  }

  set file(file: FileWithPreview | undefined) {
    this._file = file;
  }

  get error() {
    return this._error;
  }

  constructor() {
    makeAutoObservable(this);
  }

  private _fetchOptions() {
    this._options = getOptionsArray(employeeStruct, '', t);
  }

  private mapColumns() {
    this._fetchOptions();
    for (const [i, row] of this._mappingRows.entries()) {
      const rowOption = this._options.filter((o) => {
        if (
          (o.value === 'tax_data__steuerklasse' && row.column === 'tax_class') ||
          (o.value === 'tax_data__kinderfreibetrag' && row.column === 'child_allowance')
        ) {
          return true;
        }

        if (o.value === 'general_data__birth_last_name' && row.column === 'last_name') {
          return false;
        }
        return o.value.indexOf(row.column) !== -1;
      });

      if (rowOption && rowOption.length > 0) {
        this._mappingRows[i].property = rowOption[0].value;
      }
    }
    this._updateMappingObject();
    this.checkAllEmails();
  }

  private _updateMappingObject() {
    this._mappingObject = this._mappingRows.reduce(
      (acc, row) => {
        if (row.property) {
          acc[row.property] = row.column;
        }
        return acc;
      },
      {} as Record<string, string>
    );
  }

  private checkAllEmails() {
    if (this._type !== 'employees') return;
    try {
      this._error = undefined;
      let index = -1;
      this._emailColumn = this._mappingRows
        ?.find((r, i) => {
          if (r.property === 'contact_data__email') {
            index = i;
            return true;
          }
        })
        ?.address?.replace(/[0-9]/g, '');

      if (!this._emailColumn || !this._fileData) return;

      const emails = Object.keys(this._fileData || {})
        .filter((key) => {
          return (
            this._emailColumn &&
            key.startsWith(this._emailColumn) &&
            key !== this._emailColumn + '1'
          );
        })
        .map((key) => this._fileData?.[key].v);

      const wrongEmails = [];
      for (const email of emails) {
        if (email && !isEmail(email)) {
          wrongEmails.push(email);
        }
      }

      if (wrongEmails.length > 0) {
        const msg = t('import:INVALID_EMAILS', { emails: wrongEmails.join(', ') });
        this._mappingRows[index].hasError = true;
        this._mappingRows[index].errorMessage = msg;
        throw msg;
      }
    } catch (error) {
      this._error = error as string;
    }
  }

  updateRow(property: string, rowId: number) {
    this._mappingRows[rowId].property = property;
    this._error = undefined;
    this._updateMappingObject();
    this.checkAllEmails();
  }

  buildImportObject() {
    if (!this._file || !this.encoded_string || !this._mappingObject) return undefined;
    const importObj = {
      file: { name: this._file.name, encoded_string: this.encoded_string },
      column_mapping: this._mappingObject
    };

    return importObj;
  }

  get isFileUploaded() {
    return this._file !== undefined;
  }
}

export default CreateImportStore;
